import React, { ReactElement, useMemo } from 'react';

import {
  CalciteAction,
  CalciteListItem,
  CalciteOption,
  CalciteSelect,
} from '@esri/calcite-components-react';
import operators from '../../data/operators';

import FilterInput from './FilterInput';
import { CalciteSelectCustomEvent } from '@esri/calcite-components';

interface Props {
  onFilterChange: (filter: DataFilter) => void;
  filter: DataFilter;
  onDeleteFilter: () => void;
  colDefs: TypedColumn[];
  onFieldChange: (field: string, fieldType: OperatorType) => void;
}

export default function FilterItem({
  onFilterChange,
  filter,
  onDeleteFilter,
  colDefs,
  onFieldChange,
}: Props): ReactElement {
  const { filterType, field } = useMemo(
    () => colDefs.find((col) => col.field.name === filter.field) ?? colDefs[0],
    [colDefs, filter.field]
  );

  const handleFieldChange = (e: CalciteSelectCustomEvent<void>) => {
    const field = e.target.selectedOption.value as string;
    const column = colDefs.find((col) => col.field.name === field);
    if (!column) {
      throw new Error(`Could not find column for field: ${field}`);
    }
    onFieldChange(field, column.filterType);
  };

  const handleOperatorChange = (e: CalciteSelectCustomEvent<void>) => {
    onFilterChange({ ...filter, operator: e.target.selectedOption.value });
  };

  const handleValueChange = (value: FilterValue) =>
    onFilterChange({ ...filter, value });

  return (
    <CalciteListItem>
      {/* Operator select menu */}
      <div slot="content" className="flex flex-col items-stretch">
        <div className="flex">
          <CalciteSelect
            label="Field"
            value={field.name as string | undefined}
            onCalciteSelectChange={handleFieldChange}
            scale="s"
            width="half"
          >
            {colDefs.map((colDef) => (
              <CalciteOption
                key={colDef.name as string}
                label={colDef.name}
                value={colDef.field.name}
              />
            ))}
          </CalciteSelect>
          <CalciteSelect
            label="Select"
            onCalciteSelectChange={handleOperatorChange}
            placeholder="Select an operator"
            scale="s"
            width="half"
          >
            {operators[filterType].map((filterItem) => (
              <CalciteOption
                key={filterItem.alias}
                label={filterItem.alias}
                value={filterItem}
                selected={filter.operator === filterItem}
              />
            ))}
          </CalciteSelect>
        </div>
        {/* Text input for value to filter by */}
        {filter.operator && !filter.operator.omitValue && (
          <FilterInput
            type={filterType}
            value={filter.value}
            domain={field.domain}
            onChange={handleValueChange}
          />
        )}
      </div>

      {/* Filter deletion button */}
      <CalciteAction
        icon="x"
        text="delete"
        scale="s"
        onClick={() => onDeleteFilter()}
        slot="actions-end"
      />
    </CalciteListItem>
  );
}
